import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL8_BODY_LAYOUT_FIX = Object.freeze({
  firstRampa: { x: -1667.9, y: -16.0 },
  pol: { x: -734.0, y: 289.95 },
  potolok1: { x: -734.0, y: -5.0 },
  potolok2: { x: -506.0, y: -4.95 },
  potolok3: { x: -431.0, y: -4.95 },
  potolok4: { x: -356.0, y: -5.0 },
  finishPlatform: { x: 2305.25, y: 1467.45 },
});

const LEVEL8_DYNAMIC_SYMBOLS = Object.freeze({
  koleco1: {
    imagePath: "port/generated/symbols/366/png/1.png",
    width: 24.3,
    height: 24.3,
    offsetX: -0.5,
    offsetY: -0.5,
  },
  koleco2: {
    imagePath: "port/generated/symbols/366/png/1.png",
    width: 24.3,
    height: 24.3,
    offsetX: -0.5,
    offsetY: -0.5,
  },
  car: {
    imagePath: "port/generated/symbols/368/png/1.png",
    width: 99.0,
    height: 42.75,
    offsetX: -0.5,
    offsetY: -0.5,
  },
  killer1: {
    imagePath: "port/generated/symbols/379/png/1.png",
    width: 181.6,
    height: 393.4,
    offsetX: -117.9,
    offsetY: -46.7,
  },
  killer2: {
    imagePath: "port/generated/symbols/379/png/1.png",
    width: 181.6,
    height: 393.4,
    offsetX: -117.9,
    offsetY: -46.7,
  },
  killer3: {
    imagePath: "port/generated/symbols/379/png/1.png",
    width: 181.6,
    height: 393.4,
    offsetX: -117.9,
    offsetY: -46.7,
  },
});

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelEightRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_8/1.visual.runtime.svg";
      this.level8DynamicImages = Object.fromEntries(
        Object.entries(LEVEL8_DYNAMIC_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.level8DynamicImages = {};
    }

    for (const [name, fix] of Object.entries(LEVEL8_BODY_LAYOUT_FIX)) {
      const layout = this.levelData.layout.namedChildren[name];
      if (layout) {
        layout.x = fix.x;
        layout.y = fix.y;
      }
    }

    this.createBody("firstRampa", "Polygon", 0, 1, 0.2, [[[217, 189], [247, 270], [84, 4], [102, 11], [115, 24], [127, 37], [145, 60], [174, 100], [197, 143]], [[247, 270], [288, 338.95], [0, 480.95], [0, 0], [68, 0], [84, 4]], [[288, 338.95], [315.95, 365.95], [0, 480.95]], [[315.95, 365.95], [348.95, 380.95], [0, 480.95]], [[348.95, 380.95], [390.95, 386.95], [478.95, 480.95], [0, 480.95]], [[478.95, 354.95], [478.95, 480.95], [428.95, 378.95]], [[428.95, 378.95], [478.95, 480.95], [390.95, 386.95]]]);
    this.createBody("pol", "Polygon", 0, 1, 0.2, [[[683, 0], [683, 34.05], [500, 34.05], [0, 0]], [[0, 200.05], [0, 0], [500, 34.05], [500, 200.05]]]);
    this.createBody("potolok1", "Polygon", 0, 1, 0.2, [[[198, 0], [198, 200.05], [0, 200.05], [0, 0]]]);
    this.createBody("potolok2", "Polygon", 0, 1, 0.2, [[[0, 0], [45, 0], [45, 200], [0, 200]]]);
    this.createBody("potolok3", "Polygon", 0, 1, 0.2, [[[0, 0], [45, 0], [45, 200], [0, 200]]]);
    this.createBody("potolok4", "Polygon", 0, 1, 0.2, [[[0, 0], [125, 0], [125, 200], [0, 200]]]);
    this.killSpuskBody = this.createBody("killSpusk", "Polygon", 0, 1, 0.2, [[[954.95, 494.95], [963.95, 524.95], [874.95, 514.95]], [[874.95, 514.95], [963.95, 524.95], [882.95, 547.95], [792.95, 547.95], [798.95, 514.95]], [[182.95, 26], [544.95, 393.95], [522.95, 419.95], [164, 53.95], [115.95, 0]], [[0, 0], [115.95, 0], [111, 33.95], [0, 33.95]], [[111, 33.95], [115.95, 0], [164, 53.95]], [[544.95, 393.95], [646.95, 458.95], [522.95, 419.95]], [[646.95, 458.95], [798.95, 514.95], [792.95, 547.95], [632.95, 491], [522.95, 419.95]]]);
    this.carBody = this.createBody("car", "Polygon", 1, 1, 0.2, [[[98, 40.75], [91.5, 41.75], [91.5, 39.25], [98, 24.25]], [[62, 0], [68, 16.5], [65.75, 17], [59.75, 0]], [[68, 16.5], [98, 24.25], [65.75, 26], [65.75, 17]], [[17.25, 0.5], [20.5, 21.75], [13.5, 28.5], [9.5, 31.5], [0, 0.5]], [[0, 41.5], [0, 0.5], [6.5, 36.25], [5.5, 41.5]], [[6.5, 36.25], [0, 0.5], [9.5, 31.5]], [[13.5, 28.5], [20.5, 21.75], [17.25, 27.5]], [[20.5, 21.75], [33.25, 26], [17.25, 27.5]], [[33.25, 26], [65.75, 26], [21.25, 27.5], [17.25, 27.5]], [[21.25, 27.5], [65.75, 26], [25.5, 28.5]], [[65.75, 26], [98, 24.25], [28.5, 30.25], [25.5, 28.5]], [[28.5, 30.25], [98, 24.25], [75.25, 27.5], [30.75, 32.75]], [[33.75, 41.5], [33.5, 39.25], [63.5, 41.5]], [[33.5, 39.25], [32.25, 35.5], [63.5, 41.5]], [[32.25, 35.5], [30.75, 32.75], [64.5, 36.25], [63.5, 41.5]], [[64.5, 36.25], [30.75, 32.75], [67.5, 31.5]], [[67.5, 31.5], [30.75, 32.75], [71.5, 28.5]], [[71.5, 28.5], [30.75, 32.75], [75.25, 27.5]], [[75.25, 27.5], [98, 24.25], [79.25, 27.5]], [[79.25, 27.5], [98, 24.25], [83.5, 28.5]], [[83.5, 28.5], [98, 24.25], [86.5, 30.25]], [[86.5, 30.25], [98, 24.25], [88.75, 32.75]], [[88.75, 32.75], [98, 24.25], [90.25, 35.5]], [[90.25, 35.5], [98, 24.25], [91.5, 39.25]]]);
    this.killSpusk2Body = this.createBody("killSpusk2", "Polygon", 0, 1, 0.2, [[[290, 76], [631.05, 409.05], [275, 105.95], [199, 34]], [[0, 0], [199, 34], [191, 65.95], [-6, 34.95]], [[191, 65.95], [199, 34], [275, 105.95]], [[275, 105.95], [631.05, 409.05], [618, 439.95]], [[631.05, 409.05], [717.05, 457.05], [618, 439.95]], [[717.05, 457.05], [891.05, 494.05], [882.95, 523.95], [705.95, 485.95], [618, 439.95]], [[891.05, 494.05], [965.05, 494.05], [964.95, 523.95], [882.95, 523.95]], [[964.95, 523.95], [965.05, 494.05], [1049.05, 468.05], [1057.95, 493.95]]]);
    const wheel1 = this.createBody("koleco1", "Circle", 1, 1, 0.2, [23.3]);
    const wheel2 = this.createBody("koleco2", "Circle", 1, 1, 0.2, [23.3]);
    this.killer1Body = this.createBody("killer1", "Polygon", 10, 1, 0.2, [[[8.9, 0], [34.9, 0], [34.9, 200], [22.2, 218.5], [8.9, 200]]]);
    this.killer2Body = this.createBody("killer2", "Polygon", 10, 1, 0.2, [[[8.9, 0], [34.9, 0], [34.9, 200], [22.2, 218.5], [8.9, 200]]]);
    this.killer3Body = this.createBody("killer3", "Polygon", 10, 1, 0.2, [[[8.9, 0], [34.9, 0], [34.9, 200], [22.2, 218.5], [8.9, 200]]]);
    this.createBody("finishPlatform", "Polygon", 0, 1, 0.2, [[[482.85, -107], [500.05, -107], [500.05, 20], [482.85, 0]], [[0, 0], [482.85, 0], [500.05, 20], [0, 20]]]);
    this.createBody("barier", "Polygon", 0, 1, 0.2, [[[24.75, 0], [34.5, 33.75], [0, 33.75], [10, 0]]]);

    this.addPrismaticJoint(this.killer1Body, this.ground, this.killer1Body.GetWorldCenter(), new b2Vec2(0, 1));
    this.addPrismaticJoint(this.killer2Body, this.ground, this.killer2Body.GetWorldCenter(), new b2Vec2(0, 1));
    this.addPrismaticJoint(this.killer3Body, this.ground, this.killer3Body.GetWorldCenter(), new b2Vec2(0, 1));
    this.addRevoluteJoint(this.carBody, wheel1, new b2Vec2((this.levelData.layout.namedChildren.koleco1.x + 11.65) / 30, (this.levelData.layout.namedChildren.koleco1.y + 11.65) / 30));
    this.addRevoluteJoint(this.carBody, wheel2, new b2Vec2((this.levelData.layout.namedChildren.koleco2.x + 11.65) / 30, (this.levelData.layout.namedChildren.koleco2.y + 11.65) / 30));
    this.killer1Up = false;
    this.killer2Up = false;
    this.killer3Up = false;
  }

  levelUpdate() {
    if (
      this.player.y > 1800
      || this.contactListener.playerContactBodies.indexOf(this.killer1Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.killer2Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.killer3Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.killSpuskBody) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.killSpusk2Body) !== -1
    ) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    for (const [name, body, stateName] of [["killer1", this.killer1Body, "killer1Up"], ["killer2", this.killer2Body, "killer2Up"], ["killer3", this.killer3Body, "killer3Up"]]) {
      const layout = this.getDisplayLayout(name);
      if (layout.y > 70) {
        this[stateName] = true;
      }
      if (layout.y < -4) {
        this[stateName] = false;
      }
      if (this[stateName]) {
        body.SetLinearVelocity(new b2Vec2(0, -4));
      }
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    for (const name of ["koleco1", "koleco2", "car", "killer1", "killer2", "killer3"]) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawSymbolImage(
        ctx,
        this.level8DynamicImages[name],
        LEVEL8_DYNAMIC_SYMBOLS[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
